import { ITask } from '@modules/TasksModule/types';

export interface TaskTimeoutResult {
  /**
   * The earliest timeout timestamp (in milliseconds)
   */
  timeoutAt: number | null;
  /**
   * The type of timeout that will occur first
   */
  timeoutType: 'agent' | 'queue' | null;
}

/**
 * Determines which timeout (agent or queue) will occur first for a task.
 *
 * @param task - The task to check timeouts for
 * @returns An object containing the earliest timeout timestamp and its type
 */
const getTaskTimeout = (task: ITask): TaskTimeoutResult => {
  const agentTimeoutAt = task.attributes?.agentTimeoutAt;
  const queueTimeoutAt = task.attributes?.queueTimeoutAt;

  // If neither timeout is set, return null
  if (!agentTimeoutAt && !queueTimeoutAt) {
    return { timeoutAt: null, timeoutType: null };
  }

  // If only one timeout is set, return that one
  if (agentTimeoutAt && !queueTimeoutAt) {
    return { timeoutAt: agentTimeoutAt, timeoutType: 'agent' };
  }

  if (queueTimeoutAt && !agentTimeoutAt) {
    return { timeoutAt: queueTimeoutAt, timeoutType: 'queue' };
  }

  // Both timeouts are set, return the earliest one
  // TypeScript now knows both values are defined due to the checks above
  if (agentTimeoutAt && queueTimeoutAt) {
    if (agentTimeoutAt < queueTimeoutAt) {
      return { timeoutAt: agentTimeoutAt, timeoutType: 'agent' };
    }
    return { timeoutAt: queueTimeoutAt, timeoutType: 'queue' };
  }

  return { timeoutAt: null, timeoutType: null };
};

export default getTaskTimeout;
